import { IPGeolocationResult } from '@/utils/ipGeolocation';

interface CachedLocationData {
  ip: string;
  result: IPGeolocationResult;
  timestamp: number;
  expiresAt: number;
}

interface CacheConfig {
  /** Cache duration in milliseconds (default: 24 hours) */
  ttl?: number;
  /** Maximum number of cached entries (default: 50) */
  maxEntries?: number;
  /** Local storage key prefix */
  keyPrefix?: string;
}

class IPLocationCache {
  private readonly ttl: number;

  private readonly maxEntries: number;

  private readonly keyPrefix: string;

  private readonly cacheKey: string;

  constructor(config: CacheConfig = {}) {
    this.ttl = config.ttl || 24 * 60 * 60 * 1000; // 24 hours
    this.maxEntries = config.maxEntries || 50;
    this.keyPrefix = config.keyPrefix || 'terrai_ip_location';
    this.cacheKey = `${this.keyPrefix}_cache`;
  }

  /**
   * Get user's IP address (simplified approach using a service)
   */
  private async getUserIP(): Promise<string> {
    try {
      // Use a simple service to get just the IP
      const response = await fetch('https://api.ipify.org?format=json');
      if (!response.ok) throw new Error('Failed to get IP');
      
      const data = await response.json();
      return data.ip || 'unknown';
    } catch {
      // Fallback: use a hash based on browser fingerprint
      const fingerprint = [
        navigator.userAgent,
        navigator.language,
        `${window.screen.width}x${window.screen.height}`,
        new Date().getTimezoneOffset().toString(),
      ].join('|');
      
      // Simple hash function using a safer approach
      let hash = 0;
      for (let i = 0; i < fingerprint.length; i++) {
        const char = fingerprint.charCodeAt(i);
        hash = (hash * 31 + char) % 2147483647; // Use modulo instead of bitwise
      }
      
      return `fp_${Math.abs(hash).toString(36)}`;
    }
  }

  /**
   * Get all cached data from localStorage
   */
  private getCacheData(): Record<string, CachedLocationData> {
    try {
      const stored = localStorage.getItem(this.cacheKey);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  }

  /**
   * Save cache data to localStorage
   */
  private setCacheData(data: Record<string, CachedLocationData>): void {
    try {
      localStorage.setItem(this.cacheKey, JSON.stringify(data));
    } catch {
      // Storage might be full or disabled, ignore silently
    }
  }

  /**
   * Clean expired entries from cache
   */
  private cleanExpiredEntries(
    cache: Record<string, CachedLocationData>
  ): Record<string, CachedLocationData> {
    const now = Date.now();
    const cleaned: Record<string, CachedLocationData> = {};
    
    for (const [key, entry] of Object.entries(cache)) {
      if (entry.expiresAt > now) {
        cleaned[key] = entry;
      }
    }
    
    return cleaned;
  }

  /**
   * Limit cache size by removing oldest entries
   */
  private limitCacheSize(
    cache: Record<string, CachedLocationData>
  ): Record<string, CachedLocationData> {
    const entries = Object.entries(cache);
    
    if (entries.length <= this.maxEntries) {
      return cache;
    }
    
    // Sort by timestamp (oldest first)
    entries.sort(([, a], [, b]) => a.timestamp - b.timestamp);
    
    // Keep only the most recent entries
    const limited = entries.slice(-this.maxEntries);
    return Object.fromEntries(limited);
  }

  /**
   * Get cached location result for current IP
   */
  async getCached(): Promise<IPGeolocationResult | null> {
    try {
      const ip = await this.getUserIP();
      const cache = this.getCacheData();
      const entry = cache[ip];
      
      if (!entry) return null;
      
      // Check if entry is still valid
      if (entry.expiresAt <= Date.now()) {
        // Entry expired, remove it
        delete cache[ip];
        this.setCacheData(cache);
        return null;
      }
      
      return entry.result;
    } catch {
      return null;
    }
  }

  /**
   * Cache location result for current IP
   */
  async setCached(result: IPGeolocationResult): Promise<void> {
    try {
      const ip = await this.getUserIP();
      let cache = this.getCacheData();
      
      // Clean expired entries
      cache = this.cleanExpiredEntries(cache);
      
      // Add new entry
      const now = Date.now();
      cache[ip] = {
        ip,
        result,
        timestamp: now,
        expiresAt: now + this.ttl,
      };
      
      // Limit cache size
      cache = this.limitCacheSize(cache);
      
      // Save to localStorage
      this.setCacheData(cache);
    } catch {
      // Ignore cache errors silently
    }
  }

  /**
   * Clear all cached location data
   */
  clearCache(): void {
    try {
      localStorage.removeItem(this.cacheKey);
    } catch {
      // Ignore errors
    }
  }

  /**
   * Get cache statistics
   */
  async getCacheStats(): Promise<{
    totalEntries: number;
    currentIPCached: boolean;
    oldestEntry: Date | null;
    newestEntry: Date | null;
    cacheHitRate?: number;
  }> {
    try {
      const ip = await this.getUserIP();
      const cache = this.getCacheData();
      const entries = Object.values(cache);
      
      if (entries.length === 0) {
        return {
          totalEntries: 0,
          currentIPCached: false,
          oldestEntry: null,
          newestEntry: null,
        };
      }
      
      const timestamps = entries.map(e => e.timestamp);
      const oldest = Math.min(...timestamps);
      const newest = Math.max(...timestamps);
      
      return {
        totalEntries: entries.length,
        currentIPCached: !!cache[ip] && cache[ip].expiresAt > Date.now(),
        oldestEntry: new Date(oldest),
        newestEntry: new Date(newest),
      };
    } catch {
      return {
        totalEntries: 0,
        currentIPCached: false,
        oldestEntry: null,
        newestEntry: null,
      };
    }
  }

  /**
   * Check if caching is available (localStorage)
   */
  isCacheAvailable(): boolean {
    try {
      const testKey = `${this.keyPrefix}_test`;
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch {
      return false;
    }
  }
}

// Export singleton instance with default config
export const ipLocationCache = new IPLocationCache({
  ttl: 24 * 60 * 60 * 1000, // 24 hours
  maxEntries: 50,
});

// Export class for custom instances
export { IPLocationCache };

// Export types
export type { CachedLocationData, CacheConfig };